import * as React from "react";
import {
  AppBar,
  Box,
  Card,
  CardContent,
  Container,
  CssBaseline,
  Grid,
  IconButton,
  ThemeProvider,
  Toolbar,
  Typography,
  createTheme,
  CircularProgress,
  Skeleton,
  Dialog,
} from "@mui/material";
import FavoriteRoundedIcon from "@mui/icons-material/FavoriteRounded";
import ArrowBackIosNewRoundedIcon from "@mui/icons-material/ArrowBackIosNewRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { useEventData } from "../contexts/EventDataContext";
import { getAccessibleTextColor } from "../utils/colors";
import {
  collection,
  getDocs,
  query,
  orderBy,
  limit as fbLimit,
  startAfter,
  doc,
  updateDoc,
  increment,
} from "firebase/firestore";
import { db } from "../firebase/config";
import { useLocation } from "react-router-dom";
import fallbackImage from "../statics/galleryFallback.jpg";

export default function AlbumPage() {
  const { eventData, gotoHome, currentEventId } = useEventData();
  const location = useLocation();
  const isPreview = location.pathname.includes("/preview/");
  const themeData = eventData.theme || {};
  const backgroundColor = themeData.backgroundColor?.value || "#FFF7DC";
  const primaryColor = themeData.mainColor?.value || "#8B4513";
  const secondaryColor = themeData.secondaryColor?.value || "#FFD700";
  const fontColor = themeData.fontColor?.value || "#7A3E00";
  const backgroundImage = themeData.backgroundImage?.value;


  const dynamicTheme = React.useMemo(
    () =>
      createTheme({
        palette: {
          primary: {
            main: primaryColor,
            contrastText: getAccessibleTextColor(primaryColor),
          },
          secondary: {
            main: secondaryColor,
            contrastText: getAccessibleTextColor(secondaryColor),
          },
          background: { default: backgroundColor, paper: backgroundColor },
          text: { primary: fontColor },
        },
        shape: { borderRadius: 16 },
        typography: {
          fontFamily: [
            "Inter",
            "-apple-system",
            "BlinkMacSystemFont",
            "Segoe UI",
            "Roboto",
            "Helvetica",
            "Arial",
            "sans-serif",
          ].join(","),
        },
      }),
    [primaryColor, secondaryColor, backgroundColor, fontColor]
  );

  const likeConfig = eventData.album?.likeVote || {};
  const likeEnabled = likeConfig.enabled?.value !== false; // อนุญาต like แม้ใน preview
  const showLikeCount = likeConfig.showNumberOfLikes?.value !== false;

  const [items, setItems] = React.useState([]); // wishes loaded from Firestore
  // track ids user has liked (localStorage based) and items currently updating
  const [likedIds, setLikedIds] = React.useState(new Set());
  const likedIdsRef = React.useRef(likedIds);
  const [likesLoaded, setLikesLoaded] = React.useState(false);

  const title =
    eventData.eventTitle?.value ||
    eventData.eventName?.value ||
    "พิธีมงคลสมรส ณเดชน์ & ญาญ่า";


  // helper to update likedIds and ref in one go
  const setLikedIdsAndRef = React.useCallback((next) => {
    setLikedIds((prev) => {
      const value = typeof next === 'function' ? next(prev) : next;
      likedIdsRef.current = value;
      return value;
    });
  }, []);

  const [updatingLikeIds, setUpdatingLikeIds] = React.useState(new Set());
  const [loading, setLoading] = React.useState(false);
  const [initialLoading, setInitialLoading] = React.useState(true);
  const [hasMore, setHasMore] = React.useState(true);
  const [lastDoc, setLastDoc] = React.useState(null); // pagination cursor
  const [error, setError] = React.useState(null);
  const observerRef = React.useRef();

  const PAGE_SIZE = 12;

  // Load liked ids from localStorage when event id changes
  React.useEffect(() => {
    if (!currentEventId) return;
    try {
      const raw = localStorage.getItem(`liked_wishes_${currentEventId}`);
      if (raw) {
        const arr = JSON.parse(raw);
        const setVal = new Set(arr);
        likedIdsRef.current = setVal; // immediate
        setLikedIds(setVal);
      } else {
        likedIdsRef.current = new Set();
        setLikedIds(new Set());
      }
    } catch (e) {
      console.warn("Failed to read liked wishes from storage", e);
      likedIdsRef.current = new Set();
      setLikedIds(new Set());
    } finally {
      setLikesLoaded(true);
    }
  }, [currentEventId]);

  // Persist liked ids when changed (only after initial load)
  React.useEffect(() => {
    if (!currentEventId || !likesLoaded) return;
    try {
      localStorage.setItem(
        `liked_wishes_${currentEventId}`,
        JSON.stringify(Array.from(likedIds))
      );
    } catch (e) {
      console.warn("Failed to persist liked wishes", e);
    }
  }, [likedIds, currentEventId, likesLoaded]);

  // Load initial wishes from Firestore
  React.useEffect(() => {
    if (!currentEventId) return; // wait for event id

    let cancelled = false;
    async function loadInitial() {
      setInitialLoading(true);
      setError(null);
      try {
        const wishesCol = collection(db, "events", currentEventId, "wishes");
        const q = query(
          wishesCol,
          orderBy("createdAt", "desc"),
          fbLimit(PAGE_SIZE)
        );
        const snap = await getDocs(q);
        if (cancelled) return;
        const docs = snap.docs.map((d) => {
          const data = d.data();
          return {
            id: d.id,
            // Map Firestore fields -> UI fields (adjust if your schema differs)
            name: data.senderName || data.name || "Anonymous",
            message: data.wishText || data.message || "",
            image:
              data.imageUrl ||
              data.photoUrl ||
              data.image ||
              "https://via.placeholder.com/600x600?text=Wish",
            createdAt:
              data.createdAt?.toMillis?.() || data.createdAt || Date.now(),
            likes: data.likes || 0,
            liked: likedIdsRef.current.has(d.id),
          };
        });
        setItems(docs);
        setLastDoc(snap.docs[snap.docs.length - 1] || null);
        setHasMore(snap.docs.length === PAGE_SIZE);
      } catch (e) {
        console.error("Failed to load wishes", e);
        setError(e.message);
        setHasMore(false);
      } finally {
        if (!cancelled) setInitialLoading(false);
      }
    }

    loadInitial();
    return () => {
      cancelled = true;
    };
  }, [currentEventId]);

  // Load more items (pagination)
  const loadMoreItems = React.useCallback(async () => {
    if (loading || !hasMore || !currentEventId || !lastDoc) return;
    setLoading(true);
    try {
      const wishesCol = collection(db, "events", currentEventId, "wishes"); // fixed name
      const q = query(
        wishesCol,
        orderBy("createdAt", "desc"),
        startAfter(lastDoc),
        fbLimit(PAGE_SIZE)
      );
      const snap = await getDocs(q);
      const docs = snap.docs.map((d) => {
        const data = d.data();
        return {
          id: d.id,
          name: data.senderName || data.name || "Anonymous",
          message: data.wishText || data.message || "",
          image:
            data.imageUrl ||
            data.photoUrl ||
            data.image ||
            "https://via.placeholder.com/600x600?text=Wish",
          createdAt:
            data.createdAt?.toMillis?.() || data.createdAt || Date.now(),
          likes: data.likes || 0,
          liked: likedIdsRef.current.has(d.id),
        };
      });
      setItems((prev) => [...prev, ...docs]);
      setLastDoc(snap.docs[snap.docs.length - 1] || null);
      if (snap.docs.length < PAGE_SIZE) setHasMore(false);
    } catch (e) {
      console.error("Failed to load more wishes", e);
      setError(e.message);
      setHasMore(false);
    } finally {
      setLoading(false);
    }
  }, [loading, hasMore, currentEventId, lastDoc]);

  // Intersection Observer for infinite scroll
  React.useEffect(() => {
    if (initialLoading) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loading) {
          loadMoreItems();
        }
      },
      { threshold: 0.1 }
    );
    if (observerRef.current) observer.observe(observerRef.current);
    return () => {
      if (observerRef.current) observer.unobserve(observerRef.current);
    };
  }, [loadMoreItems, hasMore, loading, initialLoading]);

  // Toggle like handler
  const toggleLike = React.useCallback(
    async (id) => {
      if (!likeEnabled || !currentEventId || isPreview) return;
      const isLiked = likedIdsRef.current.has(id);
      // optimistic updates
      setItems((prev) =>
        prev.map((it) =>
          it.id === id
            ? {
              ...it,
              likes: Math.max(0, it.likes + (isLiked ? -1 : 1)),
              liked: !isLiked,
            }
            : it
        )
      );
      const newSet = new Set(likedIdsRef.current);
      if (isLiked) newSet.delete(id); else newSet.add(id);
      likedIdsRef.current = newSet;
      setLikedIds(newSet);
      setUpdatingLikeIds((s) => new Set(s).add(id));
      try {
        const ref = doc(db, "events", currentEventId, "wishes", id);
        await updateDoc(ref, { likes: increment(isLiked ? -1 : 1) });
      } catch (e) {
        console.error("Failed to update like", e);
        // revert
        setItems((prev) =>
          prev.map((it) =>
            it.id === id
              ? {
                ...it,
                likes: Math.max(0, it.likes + (isLiked ? 1 : -1)),
                liked: isLiked,
              }
              : it
          )
        );
        const revert = new Set(likedIdsRef.current);
        if (isLiked) revert.add(id); else revert.delete(id);
        likedIdsRef.current = revert;
        setLikedIds(revert);
      } finally {
        setUpdatingLikeIds((s) => {
          const ns = new Set(s); ns.delete(id); return ns;
        });
      }
    },
    [likeEnabled, currentEventId, updatingLikeIds, isPreview]
  );

  // Re-sync liked flags when likedIds changes (e.g., after navigating back and restoring from localStorage)
  React.useEffect(() => {
    if (!items.length || !likesLoaded) return;
    setItems((prev) => prev.map((it) => ({ ...it, liked: likedIdsRef.current.has(it.id) })));
  }, [likedIds, likesLoaded, items.length]);

  // Lightbox state
  const [lightboxOpen, setLightboxOpen] = React.useState(false);
  const [selectedImage, setSelectedImage] = React.useState(null);
  const [lightboxImageLoaded, setLightboxImageLoaded] = React.useState(false);

  const handleImageClick = (item) => {
    setLightboxImageLoaded(false);
    setSelectedImage(item);
    setLightboxOpen(true);
  };

  const handleCloseLightbox = () => {
    setLightboxOpen(false);
    setSelectedImage(null);
  };

  return (
    <ThemeProvider theme={dynamicTheme}>
      <CssBaseline />
      <AppBar
        position="sticky"
        color="primary"
        elevation={0}
        sx={{ bgcolor: primaryColor }}
      >
        <Toolbar sx={{ minHeight: 64 }}>
          <IconButton
            edge="start"
            color="inherit"
            aria-label="back"
            onClick={gotoHome}
          >
            <ArrowBackIosNewRoundedIcon />
          </IconButton>
          <Typography
            variant="h6"
            sx={{
              flexGrow: 1,
              textAlign: "center",
              letterSpacing: 4,
              mr: 6,
              color: getAccessibleTextColor(primaryColor),
            }}
          >
            Gallery
          </Typography>
        </Toolbar>
      </AppBar>

      <Box
        sx={{
          minHeight: "calc(100vh - 64px)",
          backgroundImage: backgroundImage ? `url(${backgroundImage})` : undefined,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundAttachment: "fixed",
          bgcolor: backgroundColor,
        }}
      >
        <Container maxWidth={false} sx={{ padding: 4, width: "100%", bgcolor: backgroundImage ? "rgba(255,255,255,0.8)" : "transparent", backdropFilter: backgroundImage ? "blur(10px)" : "none", minHeight: "calc(100vh - 64px)" }}>
          <Grid container spacing={{ xs: 2, md: 3 }}>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
                gap: "32px",
                width: "100%",
              }}
            >
              {/* Show skeleton cards during initial loading */}
              {initialLoading &&
                Array.from({ length: 12 }).map((_, index) => (
                  <Grid key={`initial-skeleton-${index}`} item>
                    <SkeletonCard />
                  </Grid>
                ))}

              {/* Show error */}
              {!initialLoading && error && (
                <Box sx={{ gridColumn: "1 / -1", textAlign: "center", py: 4 }}>
                  <Typography color="error" variant="body2">
                    Failed to load wishes: {error}
                  </Typography>
                </Box>
              )}

              {/* Show actual items when loaded */}
              {!initialLoading &&
                !error &&
                items.map((item) => (
                  <Grid key={item.id} item>
                    <Card
                      elevation={0}
                      sx={{
                        height: "100%",
                        bgcolor: "background.paper",
                        p: 1.5,
                        boxShadow: "0 2px 10px rgba(0,0,0,0.08)",
                      }}
                    >
                      <ImageSquare
                        src={item.image}
                        alt={item.name}
                        likeEnabled={likeEnabled}
                        showLikeCount={showLikeCount}
                        likes={item.likes}
                        liked={item.liked}
                        defaultBackground={backgroundImage}
                        onToggleLike={() => toggleLike(item.id)}
                        disabled={updatingLikeIds.has(item.id)}
                        onImageClick={() => handleImageClick(item)}
                      />
                      <CardContent sx={{ textAlign: "center" }}>
                        <Typography
                          variant="body2"
                          sx={{
                            color: fontColor,
                            lineHeight: 1.4,
                            letterSpacing: 0.4,
                            mb: 1,
                          }}
                        >
                          {item.message}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{ fontWeight: 600, color: fontColor }}
                        >
                          {item.name}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
            </div>
          </Grid>

          {/* Loading spinner for infinite scroll */}
          {!initialLoading && loading && (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 4, mb: 2 }}>
              <CircularProgress color="primary" />
            </Box>
          )}

          {/* Intersection observer target - only after initial loading */}
          {!initialLoading && hasMore && (
            <div
              ref={observerRef}
              style={{ height: "20px", marginTop: "20px" }}
            />
          )}

          {/* End of content message - only after initial loading */}
          {!initialLoading && !hasMore && (
            <Box sx={{ textAlign: "center", mt: 4, mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                You've reached the end of the gallery
              </Typography>
            </Box>
          )}
        </Container>

      </Box>

      {/* Lightbox Dialog */}
      <Dialog
        open={lightboxOpen}
        onClose={handleCloseLightbox}
        maxWidth="lg"
        PaperProps={{
          style: {
            backgroundColor: "transparent",
            boxShadow: "none",
            overflow: "hidden",
          },
        }}
      >
        <Box
          sx={{
            position: "relative",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            outline: "none",
            minWidth: "300px",
            minHeight: "300px",
          }}
          onClick={handleCloseLightbox}
        >
          {/* Close Button */}
          <IconButton
            onClick={handleCloseLightbox}
            sx={{
              position: "absolute",
              top: 16,
              right: 16,
              bgcolor: "rgba(0, 0, 0, 0.5)",
              color: "white",
              zIndex: 10,
              "&:hover": {
                bgcolor: "rgba(0, 0, 0, 0.7)",
              },
            }}
          >
            <CloseRoundedIcon />
          </IconButton>

          {/* Loading Indicator */}
          {!lightboxImageLoaded && (
            <CircularProgress
              size={50}
              sx={{
                color: "white",
                position: "absolute",
                zIndex: 1,
              }}
            />
          )}

          {selectedImage && (
            <img
              src={selectedImage.image ? selectedImage.image : backgroundImage ? backgroundImage : fallbackImage}
              alt={selectedImage.name}
              onLoad={() => setLightboxImageLoaded(true)}
              onError={(e) => {
                const target = e.target;
                // Delay showing fallback
                setTimeout(() => {
                  target.src = backgroundImage ? backgroundImage : fallbackImage;
                  setLightboxImageLoaded(true);
                }, 2000);
              }}
              style={{
                maxWidth: "90vw",
                maxHeight: "90vh",
                objectFit: "contain",
                borderRadius: 8,
                opacity: lightboxImageLoaded ? 1 : 0,
                transition: "opacity 0.3s ease-in-out",
              }}
            />
          )}
        </Box>
      </Dialog>

    </ThemeProvider >
  );
}

function ImageSquare({ src, alt, likes = 0, liked = false, likeEnabled = false, showLikeCount = true, onToggleLike, disabled, onImageClick, defaultBackground }) {
  const [loaded, setLoaded] = React.useState(false);
  const [error, setError] = React.useState(false);
  const clickTimerRef = React.useRef(null);
  const fallback = defaultBackground ? defaultBackground : fallbackImage;

  React.useEffect(() => {
    setLoaded(false);
    setError(false);
  }, [src]);

  React.useEffect(() => {
    return () => {
      if (clickTimerRef.current) {
        clearTimeout(clickTimerRef.current);
      }
    };
  }, []);

  const handleImageError = () => {
    // Delay showing the fallback to avoid flashing or "too fast" failure
    setTimeout(() => {
      setError(true);
      setLoaded(true);
    }, 2000);
  };

  return (
    <Box sx={{ position: "relative", width: "100%" }}>
      <Box
        sx={{
          pt: "100%",
          width: "100%",
          borderRadius: 1,
          overflow: "hidden",
          boxShadow: 1,
          position: "relative",
          bgcolor: "#f0f0f0",
          cursor: "pointer",
        }}
        onClick={(e) => {
          if (e.detail === 2) {
            // Double click detected
            if (clickTimerRef.current) {
              clearTimeout(clickTimerRef.current);
              clickTimerRef.current = null;
            }
            if (likeEnabled) onToggleLike();
          } else if (e.detail === 1) {
            // Single click detected, set a timer to differentiate from double click
            clickTimerRef.current = setTimeout(() => {
              if (!e.defaultPrevented) {
                onImageClick();
              }
              clickTimerRef.current = null;
            }, 250); // 250ms is a common double-click interval
          }
        }}
      >
        {!loaded && (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: "#f5f5f5",
            }}
          >
            <CircularProgress size={40} color="primary" thickness={4} />
          </Box>
        )}
        <img
          src={error ? fallback : src}
          alt={alt}
          onLoad={() => setLoaded(true)}
          onError={handleImageError}
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            objectFit: "cover",
            display: "block",
            transition: "opacity 0.4s ease",
            opacity: loaded ? 1 : 0,
          }}
        />
      </Box>
      {likeEnabled && (
        <Box
          sx={{
            position: "absolute",
            top: 8,
            right: 8,
            display: "flex",
            alignItems: "center",
            gap: 0.5,
          }}
        >
          <IconButton
            aria-label="like"
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onToggleLike();
            }}
            disabled={disabled}
            sx={{
              bgcolor: "rgba(255,255,255,0.95)",
              boxShadow: 1,
              border: liked ? "1px solid #e91e63" : "1px solid #ddd",
              "&:hover": { bgcolor: "rgba(255,255,255,1)" },
              opacity: disabled ? 0.6 : 1,
            }}
          >
            <FavoriteRoundedIcon
              fontSize="small"
              sx={{ color: liked ? "#e91e63" : "#d0d0d0" }}
            />
          </IconButton>
          {showLikeCount && (
            <Typography
              variant="caption"
              sx={{
                px: 0.75,
                py: 0.25,
                bgcolor: "rgba(255,255,255,0.85)",
                borderRadius: 1,
                fontWeight: 600,
                lineHeight: 1,
                boxShadow: 1,
              }}
            >
              {likes}
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
}

function SkeletonCard() {
  return (
    <Card
      elevation={0}
      sx={{
        height: "100%",
        bgcolor: "background.paper",
        p: 1.5,
        boxShadow: "0 2px 10px rgba(0,0,0,0.08)",
      }}
    >
      {/* Skeleton for image */}
      <Box sx={{ position: "relative", width: "100%" }}>
        <Skeleton
          variant="rectangular"
          sx={{
            pt: "100%",
            width: "100%",
            borderRadius: 3,
          }}
          animation="wave"
        />
        {/* Skeleton for heart icon */}
        <Box
          sx={{
            position: "absolute",
            top: 8,
            right: 8,
          }}
        >
          <Skeleton variant="circular" width={32} height={32} animation="wave" />
        </Box>
      </Box>

      {/* Skeleton for content */}
      <CardContent sx={{ textAlign: "center" }}>
        <Skeleton
          variant="text"
          height={20}
          sx={{ mb: 0.5 }}
          animation="wave"
        />
        <Skeleton
          variant="text"
          height={20}
          width="80%"
          sx={{ mb: 1, mx: "auto" }}
          animation="wave"
        />
        <Skeleton
          variant="text"
          height={16}
          width="40%"
          sx={{ mx: "auto" }}
          animation="wave"
        />
      </CardContent>
    </Card>
  );
}
